import { Head, router } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { accountSettingsPath, menuPath } from "@travily/routes";
import type AccountSettingsNotifications from "@travily/types/pages/AccountSettingsNotifications";
import { type ReactNode, useEffect, useState } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { settingsSections } from "../sections";
import { NotificationsSettingsView } from "./show.view";

const AccountSettingsNotificationsPage = ({ groups, patch_path }: AccountSettingsNotifications) => {
  // The switch is the commit, so it flips before the round-trip; the server's answer takes
  // over once props carrying that category land, reverting a write that failed.
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const clearPending = (category: string) =>
    setPending((current) => {
      if (!(category in current)) return current;
      const next = { ...current };
      delete next[category];
      return next;
    });

  const toggle = (category: string, enabled: boolean) => {
    setPending((current) => ({ ...current, [category]: enabled }));
    router.patch(
      patch_path,
      { category, enabled },
      {
        // Sync visits share one interruptible slot, so flipping a second switch inside the
        // round-trip would abort the first request and lose its write.
        async: true,
        preserveScroll: true,
        preserveState: true,
        onError: () => clearPending(category),
      },
    );
  };

  // Async responses can land out of order, so an optimistic value stands until the props
  // actually carry it; dropping it on response would revert the switch to a stale payload.
  useEffect(() => {
    const served = new Map(
      groups.flatMap((group) => group.categories.map((category) => [category.key, category.enabled] as const)),
    );

    setPending((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([category, enabled]) => served.get(category) !== enabled),
      );

      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [groups]);

  const shownGroups = groups.map((group) => ({
    ...group,
    categories: group.categories.map((category) => ({
      ...category,
      enabled: pending[category.key] ?? category.enabled,
    })),
  }));

  return (
    <>
      <Head title={t("web.accounts.settings.notifications.show.meta_title")} />
      <EditFrame
        activeSection="notifications"
        backHref={menuPath()}
        href={accountSettingsPath()}
        navLayout="row"
        sections={settingsSections()}
        title={t("web.accounts.settings.show.title")}
      >
        <NotificationsSettingsView groups={shownGroups} onToggle={toggle} />
      </EditFrame>
    </>
  );
};

AccountSettingsNotificationsPage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default AccountSettingsNotificationsPage;
